import EcommerceFulfillmentEnum from "@models/EcommerceFulfillmentEnum";
import ISupplierModel from "@models/ISupplierModel";
import ISupplierSettings from "@models/ISupplierSettings";
import IVendorModel from "@models/IVendorModel";

/*
 * Which fulfillment choices (Shipping / Local Pickup / Local Delivery) the eCommerce checkout offers.
 * Mirrors the API's EcommerceFulfillmentService, which enforces the same rules on every cart write, so
 * the checkout shows what the server will accept.
 */

type FulfillmentSupplier = Pick<ISupplierModel, "has_local_pickup" | "has_local_delivery" | "settings">;
type FulfillmentVendor = Pick<IVendorModel, "default_local_pickup" | "default_local_delivery">;

/** The fulfillment flags a cart carries. Both false = shipping; both NULL = not chosen yet (reads as shipping). */
export interface IFulfillmentFlags {
    is_local_pickup?: boolean | null;
    is_local_delivery?: boolean | null;
}

export interface IResolvedFulfillmentFlags {
    is_local_pickup: boolean;
    is_local_delivery: boolean;
}

const DEFAULT_OPTIONS: EcommerceFulfillmentEnum[] = [
    EcommerceFulfillmentEnum.SHIPPING,
    EcommerceFulfillmentEnum.LOCAL_PICKUP,
    EcommerceFulfillmentEnum.LOCAL_DELIVERY,
];

const getConfiguredOptions = (raw: ISupplierSettings | string | null | undefined): EcommerceFulfillmentEnum[] | null => {
    let settings: ISupplierSettings | null | undefined;

    try {
        settings = typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch {
        settings = null;
    }

    const configured = settings?.screens?.eCommerce?.fulfillmentOptions;

    return Array.isArray(configured) ? Array.from(new Set(configured)) : null;
};

/**
 * The choices the checkout offers, in display order: settings.screens.eCommerce.fulfillmentOptions when set,
 * otherwise all three. A local option needs its supplier flag either way. Never empty (falls back to shipping).
 */
export const getOfferedFulfillmentOptions = (supplierModel: FulfillmentSupplier): EcommerceFulfillmentEnum[] => {
    const candidates = getConfiguredOptions(supplierModel.settings) ?? DEFAULT_OPTIONS;

    const offered = candidates.filter((option) => {
        switch (option) {
            case EcommerceFulfillmentEnum.SHIPPING:
                return true;
            case EcommerceFulfillmentEnum.LOCAL_PICKUP:
                return !!supplierModel.has_local_pickup;
            case EcommerceFulfillmentEnum.LOCAL_DELIVERY:
                return !!supplierModel.has_local_delivery;
            default:
                return false;
        }
    });

    return offered.length > 0 ? offered : [EcommerceFulfillmentEnum.SHIPPING];
};

/**
 * Every choice the supplier's flags allow, in the standard order (shipping first): what the checkout offers
 * when fulfillmentOptions is not set, and what Business Settings lets the supplier pick from.
 */
export const getAvailableFulfillmentOptions = (
    supplierModel: Pick<ISupplierModel, "has_local_pickup" | "has_local_delivery">
): EcommerceFulfillmentEnum[] => {
    return getOfferedFulfillmentOptions({ ...supplierModel, settings: null as unknown as ISupplierSettings });
};

/**
 * The fulfillmentOptions value to save for a supplier's choice in Business Settings: the default first (a cart
 * starts on it unless the customer has their own default), then the other offered choices in the standard order.
 * null, which clears the setting, when that is exactly what an unconfigured checkout offers, so an option the
 * supplier turns on later is offered without another edit.
 */
export const buildFulfillmentOptionsSetting = (
    available: EcommerceFulfillmentEnum[],
    offered: EcommerceFulfillmentEnum[],
    defaultChoice: EcommerceFulfillmentEnum
): EcommerceFulfillmentEnum[] | null => {
    const others = available.filter((option) => option !== defaultChoice && offered.includes(option));
    const setting = [defaultChoice, ...others];

    const matchesUnconfigured = setting.length === available.length && setting.every((option, index) => option === available[index]);

    return matchesUnconfigured ? null : setting;
};

/** The choice a cart's flags express. Pickup wins if both are set; NULL flags read as shipping. */
export const fulfillmentFromFlags = (flags: IFulfillmentFlags | null | undefined): EcommerceFulfillmentEnum => {
    if (flags?.is_local_pickup) {
        return EcommerceFulfillmentEnum.LOCAL_PICKUP;
    }

    if (flags?.is_local_delivery) {
        return EcommerceFulfillmentEnum.LOCAL_DELIVERY;
    }

    return EcommerceFulfillmentEnum.SHIPPING;
};

export const fulfillmentFlags = (choice: EcommerceFulfillmentEnum): IResolvedFulfillmentFlags => ({
    is_local_pickup: choice === EcommerceFulfillmentEnum.LOCAL_PICKUP,
    is_local_delivery: choice === EcommerceFulfillmentEnum.LOCAL_DELIVERY,
});

/** The account's default local option when it is offered, otherwise the first offered option. */
export const getDefaultFulfillment = (
    offered: EcommerceFulfillmentEnum[],
    vendorModel?: FulfillmentVendor | null
): EcommerceFulfillmentEnum => {
    if (vendorModel?.default_local_pickup && offered.includes(EcommerceFulfillmentEnum.LOCAL_PICKUP)) {
        return EcommerceFulfillmentEnum.LOCAL_PICKUP;
    }

    if (vendorModel?.default_local_delivery && offered.includes(EcommerceFulfillmentEnum.LOCAL_DELIVERY)) {
        return EcommerceFulfillmentEnum.LOCAL_DELIVERY;
    }

    return offered[0] ?? EcommerceFulfillmentEnum.SHIPPING;
};

/**
 * The flags a cart should carry, or null when it can stay as it is. A never-chosen cart gets the default
 * choice (left NULL when that is shipping); a chosen cart keeps an offered choice and otherwise gets the default.
 */
export const getFulfillmentCorrection = (
    cart: IFulfillmentFlags,
    offered: EcommerceFulfillmentEnum[],
    vendorModel?: FulfillmentVendor | null
): IResolvedFulfillmentFlags | null => {
    const isNil = (value: boolean | null | undefined) => value === null || value === undefined;
    const notChosen = isNil(cart.is_local_pickup) && isNil(cart.is_local_delivery);

    if (!notChosen && offered.includes(fulfillmentFromFlags(cart))) {
        return null;
    }

    const defaultChoice = getDefaultFulfillment(offered, vendorModel);

    if (notChosen && defaultChoice === EcommerceFulfillmentEnum.SHIPPING) {
        return null;
    }

    return fulfillmentFlags(defaultChoice);
};
